import { colors } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Chip } from "@itandi/itandi-bb-ui/Chip";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import Link from "next/link";
import React from "react";
import { SERVER_ERROR_MESSAGE } from "~/constants/AppConst";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useMessages } from "~/hooks/vendors/deals/useMessages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import {
	getUnreadCount,
	getVendorMessageReceiver,
	UPDATE_TEMPORARY_UNREAD_MESSAGE
} from "~/models/Message";
import { canMessage } from "~/models/VendorSession";
import { PATH as DEAL_DETAIL_PATH } from "~/pages/vendors/deals/[id]/detail";
import { PATH as DEAL_MESSAGES_PATH } from "~/pages/vendors/deals/[id]/messages";
import { isSuccess } from "~/utils/api/api_handler";
import { postTemporaryUnread } from "~/utils/api/vendors/deals/Message";
import Styles from "./DealMainSpLinks.module.scss";

type Props = {
	isAdmin: boolean;
};

/**
 * スマートフォンで見た時だけ表示する詳細画面への誘導リンク
 *
 * @constructor
 * @for vendor
 */
export function DealMainSpLinksVendor({ isAdmin }: Props) {
	const { notify } = useNotification();
	const id = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: session } = useSessions().index();
	const { data: messages } = useMessages().index(id);
	const unreadMessageCount = React.useMemo((): number => {
		if (messages === null || deal === null || session === null) {
			return 0;
		}
		return getUnreadCount(messages, getVendorMessageReceiver(deal, session));
	}, [deal, messages, session]);
	if (deal === null || messages === null || session === null) {
		return null;
	}
	const handleTemporaryUnread = async (): Promise<void> => {
		const result = await postTemporaryUnread(deal.id);
		if (isSuccess(result)) {
			notify({ content: UPDATE_TEMPORARY_UNREAD_MESSAGE, variant: "success" });
			await mutateDeal?.();
			return;
		}
		notify({ content: SERVER_ERROR_MESSAGE, variant: "error" });
	};
	const unread =
		unreadMessageCount > 0 || !!deal.messageSummaryUnread?.displayUnread;
	const unreadChipText =
		unreadMessageCount > 0 ? `未読${unreadMessageCount}` : "未読";
	return (
		<div className={Styles.SPDealDetail}>
			<Link
				className={Styles.SPDealDetailLink}
				href={DEAL_DETAIL_PATH(deal.id)}
			>
				案件内容
			</Link>
			{canMessage(session, deal) && (
				<>
					<Link
						className={Styles.SPDealDetailLink}
						href={DEAL_MESSAGES_PATH(deal.id)}
					>
						管理会社とチャット
						{unread ? (
							<Chip
								background={colors["semantic-negative"]}
								borderColor={null}
								label={unreadChipText}
								textColor={colors["itandi-white"]}
							/>
						) : null}
					</Link>
					{messages && messages.length > 0 && !unread ? (
						<Button
							disabled={isAdmin}
							marginTop="1rem"
							onClick={handleTemporaryUnread}
							variant="tertiary"
							width="fill"
						>
							チャットに未読ラベルを付ける
						</Button>
					) : null}
				</>
			)}
		</div>
	);
}
